import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./stage2.css";
import { fireGameAlert } from "./gameAlert";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  normalizePlayerName,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";

const STAGE_NUM = 2;
/** old/stage2.php: rightanser == 3 triggers auto-submit. */
const MIN_RIGHT_TO_ADVANCE = 3;
/** old/stage2.php: points = points + 1 per correct click. */
const POINTS_PER_RIGHT = 1;

const SCENE_URL = `${process.env.PUBLIC_URL}/assets/game/stage2-scene.svg`;
const SCENE_URL_MOBILE = `${process.env.PUBLIC_URL}/assets/game/stage2_mob.svg`;
const POPUP_BG = `${process.env.PUBLIC_URL}/assets/game/popup-bg.png`;

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

const RIGHT_WORDS = ["conflicts", "feedback", "company"];
const WRONG_WORDS = ["customer", "network", "work"];
const ALL_WORDS = [...RIGHT_WORDS, ...WRONG_WORDS];

const RULES_TEXT =
  "The next 3 days are devoted to Goddess Lakshmi, who symbolizes wealth, fortune, and abundance. " +
  "These days emphasize that true wealth can only be achieved by adhering to ethical and righteous paths.";

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Stage2() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage2Points, setStage2Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [sceneReady, setSceneReady] = useState(false);
  const [showRules, setShowRules] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isMobilePortrait, setIsMobilePortrait] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(MOBILE_PORTRAIT_MQ).matches
      : false
  );

  const sceneRef = useRef(null);
  const answeredRef = useRef(new Set());
  const rightCountRef = useRef(0);
  /** Mirrors stage2Points synchronously — finishStage can be called in the
   * same tick as the 3rd point's setStage2Points, before React flushes that
   * state update, so it must read this ref instead of the stale state value. */
  const stage2PointsRef = useRef(0);
  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  /** Points already saved before this attempt — what Skip falls back to (old/stage2.php: initalPoints). */
  const baselinePointsRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const demoRedirectTriggeredRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
      backgroundColor: body.style.backgroundColor,
    };
    html.classList.add("stage2-lock");
    body.classList.remove("common-bg");
    body.classList.add("stage2-ethics-page");
    // old/stage2.php sets no body background image — the scene is self-contained.
    // Still color the body black: the shared header's real height doesn't line up
    // exactly with .stage2-page's calc(100dvh - 88px), leaving a sliver of body
    // background exposed at the bottom — the browser's default white otherwise.
    body.style.backgroundImage = "none";
    body.style.backgroundColor = "#fff";
    return () => {
      html.classList.remove("stage2-lock");
      body.classList.remove("stage2-ethics-page");
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
      body.style.backgroundColor = prev.backgroundColor;
    };
  }, []);

  useEffect(() => {
    const applyViewport = () =>
      setIsMobilePortrait(window.matchMedia(MOBILE_PORTRAIT_MQ).matches);
    applyViewport();
    window.addEventListener("resize", applyViewport);
    window.addEventListener("orientationchange", applyViewport);
    return () => {
      window.removeEventListener("resize", applyViewport);
      window.removeEventListener("orientationchange", applyViewport);
    };
  }, []);

  // Re-fetches the portrait-cropped scene when crossing the mobile breakpoint,
  // reapplying already-hidden statements onto the freshly injected markup.
  // Gated on dbReady: until then the component renders the "Loading game…"
  // placeholder instead of this JSX, so sceneRef.current would still be null.
  useEffect(() => {
    if (!dbReady) return undefined;
    let cancelled = false;
    const url = isMobilePortrait ? SCENE_URL_MOBILE : SCENE_URL;
    setSceneReady(false);
    fetch(url)
      .then((res) => res.text())
      .then((svgText) => {
        if (cancelled || !sceneRef.current) return;
        sceneRef.current.innerHTML = svgText;
        // Default preserveAspectRatio ("meet") letterboxes/pillarboxes whenever the
        // scene's own aspect ratio doesn't match the viewport — "slice" crops
        // instead, so the illustration fills the container edge to edge.
        const svgEl = sceneRef.current.querySelector("svg");
        if (svgEl) svgEl.setAttribute("preserveAspectRatio", "xMidYMid slice");
        answeredRef.current.forEach((word) => {
          const nodes = sceneRef.current.querySelectorAll(`[content="${word}"]`);
          nodes.forEach((node) => {
            node.style.display = "none";
          });
        });
        setSceneReady(true);
      })
      .catch((err) => console.error("Stage2 scene load:", err));
    return () => {
      cancelled = true;
    };
  }, [dbReady, isMobilePortrait]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;

    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();

    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage2Points);
  }, [stage2Points]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);

        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: normalizePlayerName(storedUser),
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));

        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage2 = Number(st.stage2) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage2);
          baselinePointsRef.current = dbStage2;
          stage2PointsRef.current = dbStage2;
          setStage2Points(dbStage2);
          setDisplayTotalScore(dbTotal);
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("Stage2 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  const finishStage = useCallback(
    async (skip = false) => {
      if (submitting || submittedRef.current) return;
      if (!skip && rightCountRef.current < MIN_RIGHT_TO_ADVANCE) return;
      submittedRef.current = true;
      setSubmitting(true);

      // old/stage2.php's send_skip() submits `initalPoints` — the points already
      // saved before this attempt, discarding whatever was earned just now.
      const stage2Score = skip ? baselinePointsRef.current : stage2PointsRef.current;
      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      const finalTotal = otherStagesTotalRef.current + stage2Score;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: STAGE_NUM,
          score: stage2Score,
          time: timeStr,
          total_score: finalTotal,
        });
        if (saveResult.total_score != null) {
          setDisplayTotalScore(saveResult.total_score);
        }
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);

      if (isDemoBypass) {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 1200));
      navigate("/stage3");
    },
    [submitting, storedUser, isDemoBypass, backendBase, adminToken, stageRowId, navigate]
  );

  const handleSkip = useCallback(() => {
    finishStage(true);
  }, [finishStage]);

  const handleSceneClick = useCallback(
    (e) => {
      if (submitting || submittedRef.current) return;
      const target = e.target.closest ? e.target.closest("[content]") : null;
      if (!target) return;
      const word = target.getAttribute("content");
      if (!word || !ALL_WORDS.includes(word)) return;
      if (answeredRef.current.has(word)) return;

      answeredRef.current.add(word);

      // old/stage2.php hides the clicked statement either way (no recolor).
      const nodes = sceneRef.current
        ? sceneRef.current.querySelectorAll(`[content="${word}"]`)
        : [];
      nodes.forEach((node) => {
        node.style.display = "none";
      });

      const isRight = RIGHT_WORDS.includes(word);
      const isFirstAnswer = answeredRef.current.size === 1;

      if (isRight) {
        rightCountRef.current += 1;
        stage2PointsRef.current += POINTS_PER_RIGHT;
        setStage2Points(stage2PointsRef.current);
      }

      if (isDemoBypass && isFirstAnswer && !demoRedirectTriggeredRef.current) {
        demoRedirectTriggeredRef.current = true;
        setTimeout(() => {
          void fireGameAlert({
            icon: "info",
            text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            window.location.assign("https://extramileplay.com/plans");
          });
        }, 400);
        return;
      }

      if (isRight && rightCountRef.current >= MIN_RIGHT_TO_ADVANCE) {
        finishStage(false);
      }
    },
    [submitting, isDemoBypass, finishStage]
  );

  if (!dbReady) {
    return <div className="stage2-loading">Loading game…</div>;
  }

  const clock = formatSecondsToClock(elapsedSec);

  return (
    <div className="stage2-page">
      <div className="stage2-scene-wrap" ref={sceneRef} onClick={handleSceneClick} />
      {!sceneReady ? <div className="stage2-scene-loading">Loading scene…</div> : null}

      <div className="stage2-hud-row">
        <div className="stage2-hud-col stage2-hud-col--left">
          <div className="stage2-timericon" aria-label={`Time ${clock}`}>
            Time: {clock}
          </div>
        </div>
        <div className="stage2-hud-col stage2-hud-col--center">
          <div className="stage2-heading-box">
            <h1 className="stage2-head-title">
              Identify statements that define ethical practices at the workplace
            </h1>
          </div>
        </div>
        <div className="stage2-hud-col stage2-hud-col--right">
          <div className="stage2-point-box">
            <h4>Score:</h4> <span>{displayTotalScore}</span>
          </div>
          <button
            type="button"
            className="stage2-timericon stage2-skip"
            disabled={submitting}
            onClick={handleSkip}
          >
            Skip
          </button>
        </div>
      </div>

      {showRules ? (
        <div className="stage2-rules-overlay" role="dialog" aria-modal="true">
          <div
            className="stage2-rules-card"
            style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,.35), rgba(0,0,0,.35)), url("${POPUP_BG}")`,
            }}
          >
            <button
              type="button"
              className="stage2-rules-close"
              aria-label="Close"
              onClick={() => setShowRules(false)}
            >
              &times;
            </button>
            <p>{RULES_TEXT}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
